import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Glass treatment (gradient fill, tinted ring, inset highlight) —
        // see the GLASS BUTTONS block in globals.css. Colors are unchanged
        // from before (primary/destructive/secondary tokens, same neutral
        // outline); only the rendering technique is new.
        default: "btn-glass btn-glass-primary",
        destructive: "btn-glass btn-glass-destructive",
        outline: "btn-glass-outline",
        secondary: "btn-glass-secondary",
        // Left as plain Tailwind: these are meant to be chromeless (toolbar
        // icons, inline text actions), and a ring + gradient on every icon
        // in a dense toolbar would look heavier than intended, not lighter.
        ghost: "rounded-md hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "btn-glass-sm",
        sm: "btn-glass-xs",
        lg: "btn-glass-md",
        icon: "btn-glass-icon",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
